import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { getWikipediaSummary } from 'util/wikiUtils'
import { transformHtmlContent, getLinkIcon } from 'util/transformHtmlContent';
import { ChevronsDown, ChevronsUp, Flag, Ellipsis, Podcast } from 'lucide-react';
import { ReactComponent as Network } from 'assets/icon-network.svg';
import EntityActions from 'components/Entities/EntityActions'
import Report from 'components/Entities/Report'
import InfluencesModal from 'components/Issues/InfluencesModal'
import { Chip } from 'ui/Chip';
import { Issue } from 'util/constants'
import EntityThumbnail from 'components/Entities/EntityThumbnail'
import EntitySubscription from 'ui/EntitySubscription'
import EntitySentiment from 'components/Alignment/EntitySentiment'
import { getIssueThumbnailUrlFromEntity } from 'util/entityUtils';
import { formatDate, formatRelativeTime } from 'config/dayjsConfig';
import { useUser } from 'util/UserContext';
import TooltipWrapper from 'ui/TooltipWrapper';
import { 
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from 'ui/DropdownMenu';
import { Drawer, DrawerContent, DrawerTrigger } from 'ui/Drawer';

interface Props {
    issue: Issue
    showMeta: boolean
    truncateDescription: boolean | null
    isIssueProfilePage?: boolean
    showActions?: boolean
    onVote?: () => void
}

// Wikipedia summary is fetched via util getWikipediaSummary which
// returns a normalized object with fields like title, extract,
// thumbnail (string | null), etc.

const IssueCard = ({
    issue,
    showMeta = true,
    truncateDescription = true,
    isIssueProfilePage = false,
}: Props) => {
    const navigate = useNavigate()
    const { currentUser, setShowAuthModal, userIsMobile } = useUser();
    const [isExpanded, setIsExpanded] = useState(false)
    const issueHref = `/issue/${encodeURIComponent(issue.cname)}`
    const [menuOpen, setMenuOpen] = useState(false)
    const [drawerOpen, setDrawerOpen] = useState(false)
    const [isReportModalOpen, setIsReportModalOpen] = useState(false)
    const [extract, setExtract] = React.useState<string | null>(null)
    const [imageUrl, setImageUrl] = React.useState<string | null>(null)
    const [isTruncated, setIsTruncated] = useState(false);
    const TruncateMarkup = require('react-truncate-markup').default;
    const [sharedImageHeight, setSharedImageHeight] = useState<number | null>(null);
    
    // States for influence modals
    const [showSubjectInfluencesModal, setShowSubjectInfluencesModal] = useState(false);
    const [showTopicInfluencesModal, setShowTopicInfluencesModal] = useState(false);

    useEffect(() => {
        const fetchSummary = async () => {
            try {
                // Use shared util, which also handles compound titles (subject~topic)
                const summary = await getWikipediaSummary(issue.cname)
                const imageSource = summary?.thumbnail ?? null
                setImageUrl(imageSource)
                setExtract(summary?.extract ?? null)
            } catch (error) {
                console.error('Error fetching Wikipedia summary:', error)
                setExtract(null)
            }
        }

        fetchSummary()
    }, [issue.cname, issue.topic_id])

    const getFallbackExtract = (isSubject: boolean): string => {
        if (isSubject) {
            return String(issue.properties?.subject_properties?.extract || issue.properties?.wiki_summary || '')
        }
        return String(issue.properties?.topic_properties?.extract || '')
    }

    const handleCardClick = (event: React.MouseEvent<HTMLDivElement>, isTopic: boolean = false) => {
        const element = event.target as HTMLElement;
        if (element.tagName.toLowerCase() === 'a') return;    
        if (element.classList.contains('read-more')) return;    
        if (element.classList.contains('card-more')) return;
        if (!isIssueProfilePage) {
            event.stopPropagation();
            navigate(issueHref);
        } else {          
            event.preventDefault();  
            if (element.tagName.toLowerCase() === 'img' || element.tagName.toLowerCase() === 'svg' || element.tagName.toLowerCase() === 'path' || element.classList.contains('no-metadata-link')) {
                event.stopPropagation();
                if (issue.topic_id) {
                    if (isTopic) {
                        navigate(`/issue/${issue.topic_cname}`);
                    } else {
                        navigate(`/issue/${issue.subject_cname}`);
                    }
                } else {
                    const externalLink = issue.wikipedia_link || issue.properties?.reference_link;
                    if (externalLink) {
                        window.open(externalLink, "_blank", "noopener,noreferrer");
                    }
                }
            } else {            
                return;
            }
        }
    };
    
    const handleDescriptionClick = (event: React.MouseEvent) => {
        if (!truncateDescription || !isTruncated) return;
      
        // Check if the click originated from a link or interactive element
        const target = event.target as HTMLElement;
        const isLinkClick = target.closest('a, button, .external-mention, .mention, [role="button"]');
        
        // Don't toggle expansion if clicking on a link or interactive element
        if (isLinkClick) {
          event.stopPropagation();
          return;
        }
        event.preventDefault();
        if (isIssueProfilePage && truncateDescription) {
            setIsExpanded(!isExpanded);
        } else {
            return;
        }
    };
    
    const handleReadMoreClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setIsExpanded(!isExpanded);
        event.stopPropagation();
    };

    const handleReportClick = () => {
      if (!currentUser) {
          setShowAuthModal(true)
      } else {
          setMenuOpen(false)
          setDrawerOpen(false)
          setIsReportModalOpen(true)
      }
    }

    const handleSubjectInfluencesClick = () => {
        setShowSubjectInfluencesModal(true);
    }

    const handleTopicInfluencesClick = () => {
        setShowTopicInfluencesModal(true);
    }

    const MoreButton = React.forwardRef<
        HTMLButtonElement,
        React.ButtonHTMLAttributes<HTMLButtonElement>
    >((props, ref) => (
        <button
            type="button"
            ref={ref}
            {...props}
            className="card-more size-7 rounded-full flex justify-center items-center"
        >
            <Ellipsis className="icon-stroke size-5"/>
        </button>
    ))
    MoreButton.displayName = 'MoreButton'

    const MoreOptions = () => (
        <>
            {!userIsMobile ? (
                <div className="flex flex-col">
                    <DropdownMenuItem onClick={handleReportClick}>
                        <div className="flex items-center gap-2 px-4 py-2 rounded-md text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
                            <Flag className="size-4 text-eoe-icon-stroke dark:text-eoe-dark-icon-stroke"/>
                            <span>Report</span>
                        </div>
                    </DropdownMenuItem>
                </div>
            ) : (
                <>
                    <button onClick={handleReportClick} className="flex w-full items-center gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
                        <Flag className="size-4"/>
                        <span>Report</span>
                    </button>
                </>
            )}
        </>
    )

    // Component for influence action button
    const InfluenceButton = ({ onClick, title, label }: { onClick: () => void; title: string; label: string }) => (
        <button
            className="h-7 rounded-full px-2 text-eoe-gray dark:text-eoe-dark-icon-stroke dark:hover:text-eoe-dark-icon-stroke-hover bg-gray-200 dark:bg-eoe-dark-icon flex justify-center items-center hover:bg-gray-300 dark:hover:bg-eoe-dark-icon-hover transition-colors text-xs"
            onClick={(e) => {
                e.stopPropagation();
                onClick();
            }}
            title={title}
        >
            <div className={`flex items-center ${label === 'Influenced' ? 'flex-row-reverse' : ''}`}>
                <Network className="size-4" />
                <span className="text-xs mx-1 font-semibold">{label}</span>
            </div>
        </button>
    )

    const truncateEllipsis = (node: any) => {
        return (
            <span className="read-more">
                <button 
                    className="read-more-border w-full text-sm rounded-md border-b-2 justify-center items-center px-1"
                    onClick={handleReadMoreClick}
                >
                    <span className="flex justify-center items-center ">
                        <ChevronsDown className="flex justify-center items-center"/>
                    </span>
                </button>
            </span>
        );
    };
    
    const renderDescription = (isSubject: boolean) => {
        if (truncateDescription && !isExpanded) {
          return (
            <TruncateMarkup 
                lines={9} 
                tokenize="words" 
                ellipsis={truncateEllipsis} 
                onTruncate={(wasTruncated: any) => setIsTruncated(wasTruncated)}
            >
              <div>
                {isSubject
                  ? transformHtmlContent(extract ?? getFallbackExtract(true))
                  : transformHtmlContent(getFallbackExtract(false))}
              </div>
            </TruncateMarkup>
          );
        } else {
            return (
                <div>
                    {isSubject
                      ? transformHtmlContent(extract ?? getFallbackExtract(true))
                      : transformHtmlContent(getFallbackExtract(false))}
                    {isTruncated && (
                        <span className="flex">
                            <button 
                                className="read-more-border w-full text-sm rounded-md border-b-2 justify-center items-center px-1"
                                onClick={handleReadMoreClick}
                            >
                                <span className="flex justify-center items-center ">
                                    <ChevronsUp className="flex justify-center items-center" />
                                </span>
                            </button>
                        </span>
                    )}
                </div>
            );
        }
    };

    const handleImageHeight = (height: number) => {
        setSharedImageHeight(prev => {
            if (prev === null || height < prev) {
                return height;
            }
            return prev;
        });
    };

    return (
        <div className="card-container my-1">
            {isReportModalOpen && (
            <Report
                entityId={issue.id}
                entityType="issue"
                open={isReportModalOpen}
                onClose={() => setIsReportModalOpen(false)}
            />
            )}
            
            {/* Subject Influences Modal - shows only influencers */}
            {showSubjectInfluencesModal && issue.subject_id && (
                <InfluencesModal
                    issueId={issue.subject_id}
                    onClose={() => setShowSubjectInfluencesModal(false)}
                    initialTab="influencer"
                />
            )}
            
            {/* Topic Influences Modal - shows only influenced */}
            {showTopicInfluencesModal && issue.topic_id && (
                <InfluencesModal
                    issueId={issue.topic_id}
                    onClose={() => setShowTopicInfluencesModal(false)}
                    initialTab="influenced"
                />
            )}
            <article>
                <div 
                    className={`rounded-lg flex flex-col px-2 md:px-3 py-1 relative ${
                        isIssueProfilePage ? 'card-main-profile-page' : 'card-main'
                    }`}
                    data-preview-container="true"
                >
                    <div className="entity-actions-container order-last">
                        <EntityActions
                            entity={issue}
                            entityType="issue"
                        />
                    </div>

                    <div className="card-content order-first">
                        {/* Title + More Icon */}
                        <div className="card-header flex flex-row justify-between">
                            {issue.topic_id ? (
                                <>
                                    {isIssueProfilePage ? (
                                        <div className="flex flex-row gap-2 flex-wrap">
                                            <Link to={`/issue/${issue.subject_cname}`} className="card-title hover:underline line-clamp-none truncate-none">
                                                {issue.subject_cname.replace(/_/g, ' ')}
                                            </Link>
                                            <span className="text-eoe-text-heading dark:text-eoe-dark-text-heading">→</span>
                                            <Link to={`/issue/${issue.topic_cname}`} className="card-title hover:underline line-clamp-none truncate-none">
                                                {issue.topic_cname.replace(/_/g, ' ')}
                                            </Link>
                                        </div>
                                    ) : (
                                        <Link to={issueHref} className="card-title hover:underline">
                                            {issue.name.replace(/~/g, ' → ')}
                                        </Link>
                                    )}
                                </>
                            ) : (
                                <>
                                    {isIssueProfilePage ? (
                                        <span className="card-title">
                                            {issue.name}
                                        </span>
                                    ) : (
                                        <Link to={issueHref} className="card-title">
                                            {issue.name}
                                        </Link>
                                    )}
                                </>
                            )}
                            <div className="relative">
                                {userIsMobile ? (
                                    <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
                                        <DrawerTrigger asChild>
                                            <MoreButton />
                                        </DrawerTrigger>
                                        <DrawerContent title="Actions">
                                            <MoreOptions />
                                        </DrawerContent>
                                    </Drawer>
                                ) : (
                                    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen} modal={false}>
                                        <DropdownMenuTrigger asChild>
                                            <MoreButton />
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                            <MoreOptions />
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                )}
                            </div>
                        </div>

                        {/* Tags/Chips row + Created Timestamp + Subscription */}
                        <div className="card-tags flex flex-row justify-between my-1 items-center">
                            <div className="flex flex-row flex-wrap gap-2 items-center">
                                {/* Sentiment bar variant */}
                                <EntitySentiment
                                    entityType="issue"
                                    entityId={issue.id}
                                    variant="bar"
                                    className="flex-shrink-0"
                                />
                                {issue.properties?.type && (
                                    
                                <TooltipWrapper title="AI-generated" arrow>
                                    <Chip
                                        variant="custom"
                                        size="sm"
                                        icon={<Podcast className="size-3" />}
                                        className="bg-eoe-entity-issue text-white"
                                    >
                                        {issue.properties.type !== 'Compound' ? issue.properties.type : 'Influence'}
                                    </Chip>
                                </TooltipWrapper>
                                )}
                                <TooltipWrapper title={formatDate(issue.created_at)} arrow>
                                <span className="card-date font-normal">
                                    {formatRelativeTime(issue.created_at)}
                                </span>
                                </TooltipWrapper>
                            </div>
                            <div className="ignore-card-click flex-shrink-0">
                                <EntitySubscription
                                    entityId={issue.id}
                                    entityType="issue"
                                    variant="mini"
                                />
                            </div>
                        </div>

                        {/* Description + Thumbnail */}
                        <div className="card-description flex flex-col-reverse gap-2" 
                            onClick={handleCardClick}>
                            <div className={`${issue.topic_id ? 'grid grid-cols-2 gap-2' : ''} ${isExpanded ? 'grid-rows-1' : ''}`}>
                                {/*
                                    Wrapper is a plain <div> (not <button>) so that:
                                    1. Grid row stretching does not vertically center the text when
                                       the sibling description is taller (native buttons apply internal
                                       align-items: center to their content when stretched).
                                    2. handleDescriptionClick's `closest('a, button, ...')` check is
                                       not tripped by the wrapper itself, preserving click-to-navigate
                                       and click-to-expand behavior.
                                    Keyboard users expand via the explicit ChevronsDown/Up button inside
                                    renderDescription and navigate via the card title link above.
                                */}
                                <div onClick={handleDescriptionClick}>
                                    {renderDescription(true)}
                                </div>
                                {/* Wikipedia link */}
                                {issue.wikipedia_link && (
                                    <div className="hidden">
                                        <a
                                            className="external-link text-xs line-clamp-1 break-all"
                                            href={issue.wikipedia_link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {decodeURIComponent(issue.wikipedia_link)}
                                        </a>
                                    </div>
                                )}
                                {issue.topic_id && (
                                    <div onClick={handleDescriptionClick}>
                                        {renderDescription(false)}
                                    </div>
                                )}

                                {issue.wikipedia_link && (
                                    <div className="flex flex-col gap-2 my-4 justify-between">
                                        <div className="inline-flex items-center gap-1">
                                            <a
                                                className="external-link text-xs inline-flex items-center"
                                                href={issue.wikipedia_link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label="Open Wikipedia"
                                            >
                                                {getLinkIcon('wikipedia', issue.wikipedia_link)}
                                            </a>
                                        <a
                                                className="external-link text-xs line-clamp-1 break-all"
                                            href={issue.wikipedia_link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                                {(issue.subject_cname ?? issue.cname ?? issue.name).replace(/_/g, ' ')}
                                        </a>
                                        </div>
                                        {issue.properties?.type === 'Compound' && (
                                            <InfluenceButton 
                                                onClick={handleSubjectInfluencesClick}
                                                title="View influencers for this subject"
                                                label="Influencers"
                                            />
                                        )}
                                    </div>
                                )}

        
                                {/* Topic Wikipedia link */}
                                {issue.topic_wikipedia_link && (
                                    <div className="flex flex-col gap-2 my-4 justify-between">
                                        <div className="inline-flex items-center gap-1">
                                            <a
                                                className="external-link text-xs inline-flex items-center"
                                                href={issue.topic_wikipedia_link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label="Open Wikipedia"
                                            >
                                                {getLinkIcon('wikipedia', issue.topic_wikipedia_link)}
                                            </a>
                                        <a
                                                className="external-link text-xs line-clamp-2"
                                            href={issue.topic_wikipedia_link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                                {(issue.topic_cname ?? issue.cname ?? issue.name).replace(/_/g, ' ')}
                                        </a>
                                        </div>
                                        {issue.properties?.type === 'Compound' && (
                                            <InfluenceButton 
                                                onClick={handleTopicInfluencesClick}
                                                title="View influenced issues for this topic"
                                                label="Influenced"
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className={`hover:cursor-pointer ${issue.topic_id ? 'grid grid-cols-2 gap-4' : 'flex flex-row'}`}>
                                <EntityThumbnail 
                                    url={getIssueThumbnailUrlFromEntity(issue, { for: 'subject', wikipediaImageUrl: imageUrl ?? undefined })}
                                    urlType="img"
                                    isProfilePage={isIssueProfilePage}
                                    onCardClick={(event) => handleCardClick(event, false)}
                                    referenceLink={issue.wikipedia_link}
                                    onHeightChange={handleImageHeight}
                                    maxHeight={sharedImageHeight || undefined}
                                />
                                {issue.topic_id && (
                                    <EntityThumbnail 
                                        url={getIssueThumbnailUrlFromEntity(issue, { for: 'topic' })}
                                        urlType="img"
                                        isProfilePage={isIssueProfilePage}
                                        onCardClick={(event) => handleCardClick(event, true)}
                                        referenceLink={issue.topic_wikipedia_link}
                                        onHeightChange={handleImageHeight}
                                        maxHeight={sharedImageHeight || undefined}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </article>
        </div>
    )
}

export default IssueCard
